import Vehicle from '@/models/Vehicle'
import { useProductCatalog } from '@/stores/product-catalog'
import { captureEvent } from '@sentry/browser'
import { merge as _merge, round as _round } from 'lodash-es'
import { acceptHMRUpdate, defineStore } from 'pinia'

export const useProductDetails = defineStore('productDetails', {
	namespaced: true,
	state: () => ({
		product: {},
		isLoading: true,

		isLightboxVisible: false,
		lightboxImageIndex: 0,

		isIncompleteOrderModalOpen: false,
		isOrderNoticeModalOpen: false,
		isSubscribing: false,
		isStartingOrder: false,
		isResumingOrder: false,
		isVehicleAvailable: true,
		existingOrder: null,
		subscriptionDuration: null,
	}),
	getters: {},
	actions: {
		updateProduct(payload) {
			// ref - https://v3.vuejs.org/guide/change-detection.html#declaring-reactive-properties
			this.setProduct({ ...this.product, ...payload })
		},
		async fetchProductByDealerCodeStockId(dealerCodeStockId) {
			this.setLoadingState(true)

			try {
				const query = Vehicle.custom(
					`v2/vehicles/${dealerCodeStockId}/browse`
				).cancelKey('browse')

				const data = await query.get()

				const vehicleItem = data[0].item

				// Remove /buy/used url returned by the API.
				// API data is served onto client-side JS for hydration purpose,
				// which was picked up by Googlebot.
				if (vehicleItem.view && vehicleItem.view.used) {
					delete vehicleItem.view.used
				}

				this.setProduct(vehicleItem)
				this.setLoadingState(false)
			} catch (e) {
				captureEvent(e) // log error to Sentry and return

				if (e.status === 404) {
					throw createError({
						statusCode: 404,
						statusMessage: `Sorry, the vehicle you're looking for is not available.`, //e.response?._data?.message,
					})
				}

				throw createError({
					statusCode: 500,
					statusMessage: 'Unexpected error',
				})
			} finally {
				this.setLoadingState(false)
			}
		},
		setLoadingState(payload) {
			this.isLoading = payload
		},
		setIsDirtyState(payload) {
			this.isDirty = payload
		},
		setProduct(payload) {
			this.product = payload
			this.setupProductImages()
		},
		setupProductImages() {
			this.product.thumbnails = this.product?.images?.filter(
				(image) => image.thumbnail
			)
			this.product.largeImages = this.product?.images?.filter(
				(image) => !image.thumbnail
			)
		},
		setSubscriptionDuration(payload) {
			this.subscriptionDuration = payload
		},
		clearProduct() {
			this.product = {}
		},
		reset() {
			this.clearProduct()
			this.setLoadingState(true)
			this.isIncompleteOrderModalOpen = false
			this.isOrderNoticeModalOpen = false
			this.isSubscribing = false
			this.isStartingOrder = false
			this.isResumingOrder = false
			this.isVehicleAvailable = true
			this.existingOrder = null
		},
		resetAllStates() {
			this.isResumingOrder = false
			this.isStartingOrder = false
			this.isSubscribing = false
			this.isIncompleteOrderModalOpen = false
			this.isOrderNoticeModalOpen = false
		},
		async checkExistingOrders() {
			const productCatalog = useProductCatalog()
			const route = useRoute()
			const runtimeConfig = useRuntimeConfig()

			this.isSubscribing = true

			this.gtmTrack({
				event: 'subscribe_now',
				product_category: 'Subscription',
				product: this.product,
				page: productCatalog.productsPagination.currentPage,
				subscription_duration: this.subscriptionDuration,
				subscription_car_primary_url:
					runtimeConfig.public.APP_URL + route.path,
			})

			if (isLoggedIn()) {
				// check if pre-oder exists
				const { data } = await useFetchForApi('/v2/pre-orders')

				if (data[0]?.continue_url) {
					this.existingOrder = data[0]
					this.isIncompleteOrderModalOpen = true
					return
				}
			}

			// if pre-order does not exists, show default OrderNotice modal
			this.onModalOpen()
		},
		onModalOpen() {
			this.isOrderNoticeModalOpen = true
			this.isSubscribing = true
			this.isStartingOrder = false
			this.isResumingOrder = false
			this.isVehicleAvailable = true
		},
		onModalClose() {
			if (this.isStartingOrder || this.isResumingOrder) {
				return
			}
			this.resetAllStates()
		},
		async onResumeOrder(orderType = 'newOrder') {
			const isResuming = orderType === 'resumeOrder'

			this.isResumingOrder = isResuming
			this.isStartingOrder = !isResuming

			const checkoutPath = useCheckoutPath()(this.existingOrder)
			const targetUrl =
				checkoutPath ||
				this.existingOrder.continue_url ||
				this.existingOrder.order_url

			try {
				await new Promise((resolve) => setTimeout(resolve, 600))

				this.resetAllStates()

				await navigateTo(targetUrl, { external: !checkoutPath })
			} catch (error) {
				console.error('Navigation failed:', error)
			}
		},
		async onStartNewOrder() {
			this.isVehicleAvailable = true
			this.isSubscribing = true
			this.isStartingOrder = true

			await this.cancelExistingOrder()

			try {
				const data = await this.checkVehicleAvailability()

				if (data?.status === false) {
					this.isSubscribing = false
					this.isVehicleAvailable = false
					return
				}
			} catch (error) {
				if (process.env.NODE_ENV === 'development') {
					console.warn('Warning error:', error)
				}
			}

			await this.createPresaleAndRedirect()
		},
		async createPresaleAndRedirect() {
			this.isSubscribing = true

			// Check if referral code exists.
			// Then pass it into /register to label the user (lead).
			const affiliateRefCode = useState('affiliate_ref_state')
			const hasRefQuery = affiliateRefCode.value

			const productCatalog = useProductCatalog()

			const { data } = await useFetchForApi(
				`v2/vehicles/${this.product.uuid}/subscribe`,
				{
					method: 'POST',
					query: {
						sp: productCatalog.productsPagination.currentPage,
						term: this.subscriptionDuration,
						...(hasRefQuery && {
							ref: affiliateRefCode.value,
						}),
					},
				}
			)

			this.existingOrder = data
			this.onResumeOrder()
		},
		async checkVehicleAvailability() {
			const { data } = await useFetchForApi(
				`v2/vehicles/${this.product.uuid}/check-availability`,
				[]
			)
			return data
		},
		async cancelExistingOrder() {
			if (!this.existingOrder?.uuid) {
				return
			}

			await useFetchForApi(
				`v2/presales/${this.existingOrder.uuid}/cancel`,
				{
					method: 'PATCH',
				}
			)
		},
		gtmTrack(payload) {
			const data = {
				event: payload.event,
				product_category: payload.product_category ?? 'Subscription',
				make: payload.product.make,
				model: payload.product.model,
				body_type: payload.product.body_type,
				location: payload.product.location,
				year: payload.product.year,
				transmission:
					payload.product.transmission === 'Manual'
						? 'Manual'
						: 'Automatic',
				fuel_type: payload.product.fuel_type,
				seat_capacity: payload.product.redbook_details?.seat_capacity,
				drive_type: payload.product.drive_type,
				colour: payload.product.colour,
				stock_id: payload.product.stock_id,
				page: payload.page,
			}

			if (payload.product_category === 'Subscription') {
				_merge(data, {
					new_or_used: payload.product.condition,
					price_year_1: payload.product.pricing.loyalty[0].price,
					price_year_2: payload.product.pricing.loyalty[1].price,
					price_year_3: payload.product.pricing.loyalty[2].price,
					upfront_fee: payload.product.upfront_cost,
					subscription_car_hero_image_url:
						payload.product.primary_image,
					subscription_car_primary_url:
						payload.subscription_car_primary_url ?? '',
					total_min_cost: payload.product.total_min_cost,
				})
				if (typeof payload.subscription_duration !== 'undefined') {
					_merge(data, {
						subscription_duration: payload.subscription_duration,
					})
				}
				if (payload.event === 'subscribe_now') {
					_merge(data, {
						subscription_duration: payload.subscription_duration,
					})
				}
			} else {
				_merge(data, {
					doors: payload.product.door_count,
					odometer: payload.product.odometer,
					drive_away_price: payload.product.retail.pricing
						.show_drive_away_price
						? _round(
								payload.product.retail.pricing
									.price_inc_drive_away,
								2
							).toFixed(2)
						: null,
					price_ex_govt_charges: !payload.product.retail.pricing
						.show_drive_away_price
						? payload.product.retail.pricing.price_ex_drive_away
						: null,
					buy_used_car_hero_image_url: payload.product.primary_image,
					buy_used_car_primary_url:
						payload.buy_used_car_primary_url ?? '',
				})

				if (typeof payload.first_name !== 'undefined') {
					_merge(data, {
						first_name: payload.first_name,
						last_name: payload.last_name,
						email: payload.email,
						phone: payload.phone,
					})
				}
				if (typeof payload.state !== 'undefined') {
					_merge(data, {
						state: payload.state,
					})
				}
				if (typeof payload.deposit_value !== 'undefined') {
					_merge(data, {
						deposit_value: payload.deposit_value,
					})
				}
			}

			if (typeof payload.features !== 'undefined') {
				_merge(data, {
					features: payload.features,
				})
			}

			if (typeof payload.search !== 'undefined') {
				data.search_value = payload.search
			}

			trackGtmEvent(data)
		},
	},
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useProductDetails, import.meta.hot))
}
